import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import DatePicker from '../../../components/forms/formDatePicker';
import FormInput from '../../../components/forms/formInput';
import { getStatusOptions } from '../../../constants/jobStatus';

const FilterIcon = () => (
    <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);
const ChevIcon = () => (
    <svg className="ic chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

// This component is also reused as-is by Expenses Reports, whose data has no
// status concept at all — showStatus defaults to false so that page is
// completely unaffected; only Jobs Performance opts in.
const JobsPerformanceFilter = ({ isFiltering, onFilter, showStatus = false }) => {

    const [isShow, setIsShow] = useState(false)
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [status, setStatus] = useState('')
    const queryClient = useQueryClient()
    const { t } = useTranslation();

    const activeCount = [dateFrom, dateTo, showStatus && status].filter(Boolean).length

    const handleFilter = (e) => {
        e.preventDefault()
        e.stopPropagation()

        // Fixed-position array — a variable-length one (previously only pushed
        // whichever dates were set) misaligns startDate/endDate whenever only
        // "Date To" is picked, since the page spreads this positionally.
        const _filters = [
            dateFrom ? new Date(dateFrom.setHours(0, 0, 0, 0)) : '',
            dateTo ? new Date(dateTo.setHours(23, 59, 59, 999)) : '',
        ]
        if (showStatus) _filters.push(status)

        onFilter(_filters)
    }

    const handleReset = () => {
        onFilter(showStatus ? ['', '', ''] : ['', ''])
        setDateFrom('')
        setDateTo('')
        setStatus('')
        queryClient.invalidateQueries(['jobs-performance'])
    }

    return (
        <>
            <button type="button" className={`filter-toggle${isShow ? ' on' : ''}`} onClick={() => setIsShow(s => !s)}>
                <FilterIcon /> {t('reports.filters')}
                {activeCount > 0 && <span className="cnt">{activeCount}</span>}
                <ChevIcon />
            </button>

            {isShow && (
                <Form onSubmit={handleFilter} className="filter-panel">
                    <div className="filter-grid">
                        <DatePicker className="form-control" label={t('reports.dateFrom')} placeholderText={t('reports.filterDateFrom')}
                            selected={dateFrom} onChange={setDateFrom} maxDate={dateTo || undefined} dateFormat="dd.MM.yyyy" />
                        <DatePicker className="form-control" label={t('reports.dateTo')} placeholderText={t('reports.filterDateTo')}
                            selected={dateTo} onChange={setDateTo} minDate={dateFrom || undefined} dateFormat="dd.MM.yyyy" />
                        {showStatus && (
                            <Form.Group>
                                <Form.Label>{t('reports.statusFilter')}</Form.Label>
                                <FormInput value={status} as="select" onChange={(e) => setStatus(e.target.value)}>
                                    <option value="">{t('reports.completedDefault')}</option>
                                    {getStatusOptions().map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </FormInput>
                            </Form.Group>
                        )}
                    </div>

                    <div className="filter-foot">
                        <span className="meta">
                            {activeCount > 0 ? t('reports.filtersSelected', { count: activeCount }) : t('reports.noFiltersApplied')}
                        </span>
                        <div className="sp">
                            <button type="button" className="btn-sbr btn-ghost-sbr btn-sm-sbr" onClick={handleReset}>{t('reports.reset')}</button>
                            <button type="submit" className="btn-sbr btn-primary-sbr btn-sm-sbr" disabled={isFiltering}>
                                {isFiltering ? t('reports.filtering') : t('reports.applyFilters')}
                            </button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default JobsPerformanceFilter
